/*
 * Copyright 2026 EquiSoft
 */
import { ThemeStructure } from "@dit/ui-components.react.theme";
import { DefaultTheme } from "styled-components";
import {
    fetchTenantTheme as fetchDitTenantTheme,
    fetchV1TenantTheme,
} from "@app/services/openapi/userexperience/fetchTenantTheme";
import ditFallbackTheme from "@app/styles/theme/theme.json";
import { theme as v1FallbackTheme } from "@app/styles/theme";

const APPLICATION_NAME = "sync";
const V1_THEME_APPLICATION_KEY = "Portal";
const I18N_CACHE_KEY = "i18nextLng";
const THEME_FETCH_TIMEOUT_MS = 3000;

export interface AppThemes {
    v1Theme: DefaultTheme;
    ditTheme: ThemeStructure;
}

function withTimeout<T>(promise: Promise<T>, fallback: T, label: string): Promise<T> {
    let timeoutId: NodeJS.Timeout | undefined;
    const timeout = new Promise<T>(resolve => {
        timeoutId = setTimeout(() => {
            console.warn(`[UX Service] ${label} timed out after ${THEME_FETCH_TIMEOUT_MS}ms, using fallback theme`);
            resolve(fallback);
        }, THEME_FETCH_TIMEOUT_MS);
    });
    return Promise.race([promise.finally(() => clearTimeout(timeoutId)), timeout]);
}

export async function initTheme(tenantKey?: string): Promise<AppThemes> {
    if (!tenantKey) {
        return { v1Theme: v1FallbackTheme, ditTheme: ditFallbackTheme };
    }

    // no access to the i18n instance at this point, use cached or browser language as fallback
    const language = (localStorage.getItem(I18N_CACHE_KEY) ?? navigator.language).split("-")[0];

    const [ditThemeResult, v1ThemeResult] = await Promise.all([
        withTimeout(
            fetchDitTenantTheme(tenantKey, APPLICATION_NAME, ditFallbackTheme),
            ditFallbackTheme,
            "fetchTenantTheme"
        ),
        withTimeout(
            fetchV1TenantTheme(tenantKey, V1_THEME_APPLICATION_KEY, language, v1FallbackTheme),
            v1FallbackTheme,
            "fetchV1TenantTheme"
        ),
    ]);

    return { v1Theme: v1ThemeResult, ditTheme: ditThemeResult };
}
